/**
 * SearchPayments slice — generic, filter-driven payment search.
 *
 * searchPayments(input): POST /payments/search via beaconApi; stores the raw
 * SearchPayments summaries + total count. Callers supply the SearchPaymentsInput
 * (filters/pagination/sort). The artifact detail page uses it with
 * filters.paymentIdentifiers = [artifactId]; other callers can reuse it with
 * any filter.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ApiPaymentSummary, SearchPaymentsInput } from './searchPaymentsApi';
import { beaconApi } from '../../../services/beaconApi';
import { RootState } from '../../store';

export interface SearchPaymentsState {
  payments: ApiPaymentSummary[];
  total: number;
  loading: boolean;
  error: string | null;
  currentRequestId: string | null;
  /** Last search input; re-dispatched on region change. */
  lastInput: SearchPaymentsInput | null;
}

export const searchPaymentsInitialState: SearchPaymentsState = {
  payments: [],
  total: 0,
  loading: false,
  error: null,
  currentRequestId: null,
  lastInput: null,
};

interface FetchResult {
  payments: ApiPaymentSummary[];
  total: number;
}

export const searchPayments = createAsyncThunk<
  FetchResult, SearchPaymentsInput, { state: RootState; rejectValue: string }
>('searchPayments/run', async (input, { getState, rejectWithValue }) => {
  const { apiBase } = getState().env;
  try {
    const response = await beaconApi.searchPayments(input, apiBase);
    return { payments: response.payments ?? [], total: response.totalPaymentsCount ?? 0 };
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Failed to search payments');
  }
});

const searchPaymentsSlice = createSlice({
  name: 'searchPayments',
  initialState: searchPaymentsInitialState,
  reducers: {
    clearSearchPayments: () => searchPaymentsInitialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchPayments.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
        state.lastInput = action.meta.arg;
      })
      .addCase(searchPayments.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loading = false;
        state.payments = action.payload.payments;
        state.total = action.payload.total;
      })
      .addCase(searchPayments.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loading = false;
        state.error = action.payload || action.error.message || 'Failed to search payments';
      });
  },
});

export const { clearSearchPayments } = searchPaymentsSlice.actions;
export default searchPaymentsSlice.reducer;
